import { STRAPI_URL, flattenStrapiEntry, parseErrorMessage, strapiAuthHeaders } from './strapi';

export type PermissionAccessMaster = {
  numericId: number | null;
  documentId: string | null;
  access_code: string;
  access_name: string;
  description: string;
  /** In-app route for the drawer (e.g. `/quotations`). */
  nav_path: string;
};

export type PermissionGroupMaster = {
  numericId: number | null;
  documentId: string | null;
  group_name: string;
  accesses: PermissionAccessMaster[];
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function unwrapDataArray(v: unknown): Record<string, unknown>[] {
  if (!v) return [];
  if (Array.isArray(v)) return v as Record<string, unknown>[];
  const o = asRecord(v);
  if (o && Array.isArray(o.data)) return o.data as Record<string, unknown>[];
  return [];
}

function normalizeAccessRow(raw: Record<string, unknown>): PermissionAccessMaster {
  const s = flattenStrapiEntry(raw);
  const rid = raw.id ?? s.id;
  const d = raw.documentId ?? s.documentId;
  return {
    numericId: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
    documentId: d != null ? String(d) : null,
    access_code: String(s.access_code ?? ''),
    access_name: String(s.access_name ?? ''),
    description: String(s.description ?? ''),
    nav_path: String(s.nav_path ?? ''),
  };
}

function normalizeGroupRow(raw: Record<string, unknown>): PermissionGroupMaster {
  const s = flattenStrapiEntry(raw);
  const rid = raw.id ?? s.id;
  const d = raw.documentId ?? s.documentId;
  const accessesRaw = unwrapDataArray(raw.permission_accesses ?? s.permission_accesses);
  return {
    numericId: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
    documentId: d != null ? String(d) : null,
    group_name: String(s.group_name ?? ''),
    accesses: accessesRaw.map((row) => normalizeAccessRow(row)),
  };
}

/** Groups with nested accesses (master-data screen). */
export async function strapiListPermissionGroupsMaster(jwt: string): Promise<PermissionGroupMaster[]> {
  /**
   * Do not use `populate[permission_accesses]=*`: Strapi expands every relation on each access
   * (including `permission_group`) and returns ValidationError:
   * `Invalid key permission_group at permission_accesses.permission_group`.
   * Shallow populate loads related access rows without nesting `permission_group` again.
   */
  const base = {
    'pagination[pageSize]': '200',
    'pagination[page]': '1',
    sort: 'group_name:asc',
  } as const;
  const populateAttempts = [
    new URLSearchParams({ ...base, 'populate[permission_accesses]': 'true' }),
    new URLSearchParams({ ...base, 'populate[0]': 'permission_accesses' }),
  ];

  let lastMessage = '';
  for (const params of populateAttempts) {
    const res = await fetch(`${STRAPI_URL}/api/permission-groups?${params}`, {
      headers: strapiAuthHeaders(jwt),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    if (res.ok) {
      const b = asRecord(body);
      if (!b?.data || !Array.isArray(b.data)) {
        return [];
      }
      return (b.data as Record<string, unknown>[]).map((row) => normalizeGroupRow(row));
    }
    lastMessage = parseErrorMessage(body, res.status);
  }
  throw new Error(lastMessage || 'Unable to load permission groups');
}

function parseCreatedDocumentId(body: unknown): string {
  const b = asRecord(body);
  const entry = b?.data && typeof b.data === 'object' ? (b.data as Record<string, unknown>) : null;
  if (!entry) throw new Error('Invalid create response');
  const s = flattenStrapiEntry(entry);
  const d = entry.documentId ?? s.documentId;
  if (d == null) throw new Error('Created entry missing documentId');
  return String(d);
}

export async function strapiCreatePermissionGroup(jwt: string, group_name: string): Promise<string> {
  const res = await fetch(`${STRAPI_URL}/api/permission-groups`, {
    method: 'POST',
    headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: { group_name: group_name.trim() } }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  return parseCreatedDocumentId(body);
}

export async function strapiUpdatePermissionGroup(
  jwt: string,
  documentId: string,
  group_name: string,
): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/permission-groups/${encodeURIComponent(documentId)}`, {
    method: 'PUT',
    headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: { group_name: group_name.trim() } }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiDeletePermissionGroup(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/permission-groups/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiCreatePermissionAccess(
  jwt: string,
  input: {
    permissionGroupDocumentId: string;
    permissionGroupNumericId?: number | null;
    access_code: string;
    access_name: string;
    description?: string;
    nav_path?: string;
  },
): Promise<void> {
  const docId = input.permissionGroupDocumentId;
  const relAttempts: Record<string, unknown>[] = [
    { permission_group: docId },
    { permission_group: { connect: [docId] } },
    ...(input.permissionGroupNumericId != null
      ? [{ permission_group: input.permissionGroupNumericId }]
      : []),
  ];
  const base = {
    access_code: input.access_code.trim(),
    access_name: input.access_name.trim(),
    description: (input.description ?? '').trim(),
    nav_path: (input.nav_path ?? '').trim(),
  };
  let last = '';
  for (const rel of relAttempts) {
    const res = await fetch(`${STRAPI_URL}/api/permission-accesses`, {
      method: 'POST',
      headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: {
          ...base,
          ...rel,
        },
      }),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    if (res.ok) return;
    last = parseErrorMessage(body, res.status);
  }
  throw new Error(last || 'Unable to create permission access');
}

export async function strapiUpdatePermissionAccess(
  jwt: string,
  documentId: string,
  input: {
    access_code?: string;
    access_name?: string;
    description?: string;
    nav_path?: string;
    permissionGroupDocumentId?: string;
  },
): Promise<void> {
  const data: Record<string, unknown> = {};
  if (input.access_code !== undefined) data.access_code = input.access_code.trim();
  if (input.access_name !== undefined) data.access_name = input.access_name.trim();
  if (input.description !== undefined) data.description = input.description.trim();
  if (input.nav_path !== undefined) data.nav_path = input.nav_path.trim();

  const attempts: Record<string, unknown>[] = [];
  const gid = input.permissionGroupDocumentId;
  if (gid !== undefined) {
    attempts.push({ ...data, permission_group: gid });
    attempts.push({ ...data, permission_group: { connect: [gid] } });
  } else if (Object.keys(data).length > 0) {
    attempts.push({ ...data });
  } else {
    return;
  }

  let last = '';
  for (const slice of attempts) {
    const res = await fetch(`${STRAPI_URL}/api/permission-accesses/${encodeURIComponent(documentId)}`, {
      method: 'PUT',
      headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: slice }),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    if (res.ok) return;
    last = parseErrorMessage(body, res.status);
  }
  throw new Error(last || 'Unable to update permission access');
}

export async function strapiDeletePermissionAccess(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/permission-accesses/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}
